/**
 * Permissions Utility
 * Helper functions to work with permission data
 */

import { TabPermission } from '../services/apiEndpoints';

/**
 * Permission levels enum for better type safety
 */
export enum PermissionLevel {
  NONE = '0',
  VIEW = '1',
  EDIT = '2',
  YES = '1',
  NO = '0',
}

/**
 * Get all tabs user has access to (value > 0)
 * @param permissions - Array of tab permissions
 * @returns Array of main_tab names the user has access to
 */
export const getAccessibleTabs = (permissions: TabPermission[]): string[] => {
  return permissions
    .filter(tab => tab.value !== '0' && tab.value !== null)
    .map(tab => tab.main_tab.toUpperCase());
};

/**
 * Check if user has access to a specific tab
 * @param permissions - Array of tab permissions
 * @param tabName - Name of the tab to check (case-insensitive)
 * @returns true if user has any level of access
 */
export const hasTabAccess = (
  permissions: TabPermission[],
  tabName: string
): boolean => {
  const tab = permissions.find(
    t => t.main_tab.toUpperCase() === tabName.toUpperCase()
  );
  return tab ? tab.value !== '0' && tab.value !== null : false;
};

/**
 * Get permission level for a specific tab
 * @param permissions - Array of tab permissions
 * @param tabName - Name of the tab (case-insensitive)
 * @returns Permission value ('0', '1', '2', etc.) or null if not found
 */
export const getTabPermissionLevel = (
  permissions: TabPermission[],
  tabName: string
): string | null => {
  const tab = permissions.find(
    t => t.main_tab.toUpperCase() === tabName.toUpperCase()
  );
  return tab ? tab.value : null;
};

/**
 * Check if user has view permission for a tab
 * @param permissions - Array of tab permissions
 * @param tabName - Name of the tab (case-insensitive)
 * @returns true if user can view (value >= 1)
 */
export const canViewTab = (
  permissions: TabPermission[],
  tabName: string
): boolean => {
  const level = getTabPermissionLevel(permissions, tabName);
  return level !== null && level !== '0' && parseInt(level) >= 1;
};

/**
 * Check if user has edit permission for a tab
 * @param permissions - Array of tab permissions
 * @param tabName - Name of the tab (case-insensitive)
 * @returns true if user can edit (value >= 2)
 */
export const canEditTab = (
  permissions: TabPermission[],
  tabName: string
): boolean => {
  const level = getTabPermissionLevel(permissions, tabName);
  return level !== null && parseInt(level) >= 2;
};

/**
 * Get all permissions for a specific main_tab
 * Useful when multiple features share the same main_tab (e.g., "hotels")
 * @param permissions - Array of tab permissions
 * @param mainTab - The main_tab value (case-insensitive)
 * @returns Array of permissions for that main_tab
 */
export const getPermissionsByMainTab = (
  permissions: TabPermission[],
  mainTab: string
): TabPermission[] => {
  return permissions.filter(
    p => p.main_tab.toUpperCase() === mainTab.toUpperCase()
  );
};

/**
 * Check if user has access to a specific feature by name
 * @param permissions - Array of tab permissions
 * @param featureName - Exact feature name (e.g., "Hotel Assessment", "GBTA Data")
 * @returns true if user has access to this specific feature
 */
export const hasFeatureAccess = (
  permissions: TabPermission[],
  featureName: string
): boolean => {
  const feature = permissions.find(p => p.name === featureName);
  return feature ? feature.value !== '0' && feature.value !== null : false;
};

/**
 * Get permission level for a specific feature
 * @param permissions - Array of tab permissions
 * @param featureName - Exact feature name
 * @returns Permission value or null if not found
 */
export const getFeaturePermissionLevel = (
  permissions: TabPermission[],
  featureName: string
): string | null => {
  const feature = permissions.find(p => p.name === featureName);
  return feature ? feature.value : null;
};

/**
 * Check if user can edit a specific feature
 * @param permissions - Array of tab permissions
 * @param featureName - Exact feature name
 * @returns true if user can edit this feature
 */
export const canEditFeature = (
  permissions: TabPermission[],
  featureName: string
): boolean => {
  const level = getFeaturePermissionLevel(permissions, featureName);
  return level !== null && parseInt(level) >= 2;
};

/**
 * Group permissions by main_tab
 * @param permissions - Array of tab permissions
 * @returns Object with main_tab as keys and arrays of permissions as values
 */
export const groupPermissionsByMainTab = (
  permissions: TabPermission[]
): Record<string, TabPermission[]> => {
  return permissions.reduce((acc, perm) => {
    const mainTab = perm.main_tab.toUpperCase();
    if (!acc[mainTab]) {
      acc[mainTab] = [];
    }
    acc[mainTab].push(perm);
    return acc;
  }, {} as Record<string, TabPermission[]>);
};

/**
 * Get human-readable permission label
 * @param permissions - Array of tab permissions
 * @param tabName - Name of the tab
 * @returns Human-readable label (e.g., "View", "Edit", "Yes")
 */
export const getPermissionLabel = (
  permissions: TabPermission[],
  tabName: string
): string | null => {
  const tab = permissions.find(
    t => t.main_tab.toUpperCase() === tabName.toUpperCase()
  );
  
  if (!tab) return null;
  
  const option = tab.perm.find(p => p.value === tab.value);
  return option ? option.label : null;
};

/**
 * Example usage in components:
 * 
 * const { permissions } = useAuth();
 * 
 * // Check basic tab access
 * if (hasTabAccess(permissions, 'hotels')) {
 *   // User can see hotels tab
 * }
 * 
 * // Check edit permission
 * if (canEditTab(permissions, 'hotels')) {
 *   // Show edit buttons
 * }
 * 
 * // Check specific feature
 * if (hasFeatureAccess(permissions, 'GBTA Data')) {
 *   // Show GBTA section
 * }
 * 
 * // Get permission level
 * const level = getTabPermissionLevel(permissions, 'hotels');
 * if (level === '2') {
 *   // Full edit access
 * }
 */
